import { useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import Layout from '../Layout'
import { useLocale } from '../../../lib/LocaleContext'

const COMMON_TIMEZONES = [
  'UTC',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Caracas',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'America/Guayaquil',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/Managua',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/Madrid',
  'Europe/London',
  'Pacific/Auckland',
]

const INPUT_CLS =
  'w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 ' +
  'placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600'

const LABEL_CLS = 'block text-xs font-mono text-zinc-400 mb-1'

export default function Edit() {
  const { props } = usePage()
  const notice = props.flash?.notice
  const user = props.auth?.user ?? {}
  const profile = props.profile ?? {}
  const { t } = useLocale()

  const { data, setData, patch, processing, errors } = useForm({
    display_name: user.display_name ?? '',
    bio: profile.bio ?? '',
    timezone: profile.timezone ?? 'UTC',
    notification_preferences: profile.notification_preferences ?? { weekly_digest: false, session_summary_email: false },
    preferred_locale: profile.preferred_locale ?? user.preferred_locale ?? 'en',
    avatar: null,
  })

  const fileRef = useRef(null)
  const [previewUrl, setPreviewUrl] = useState(user.avatar_url ?? null)

  function handleFileChange(e) {
    const file = e.target.files?.[0] ?? null
    setData('avatar', file)
    if (file) {
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  function handleNotifToggle(key) {
    setData('notification_preferences', {
      ...data.notification_preferences,
      [key]: !data.notification_preferences[key],
    })
  }

  function handleSubmit(e) {
    e.preventDefault()
    patch('/settings/profile')
  }

  function initials(nameOrEmail) {
    if (!nameOrEmail) return '?'
    const parts = nameOrEmail.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return nameOrEmail.slice(0, 2).toUpperCase()
  }

  return (
    <Layout active="profile">
      <div className="max-w-lg">
        <h2 className="text-sm font-mono font-bold text-zinc-300 mb-6">{t('settings.profile.title')}</h2>

        {notice && (
          <div className="mb-4 px-4 py-2 bg-emerald-950 border border-emerald-800 rounded-lg text-sm text-emerald-400 font-mono">
            {notice}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" encType="multipart/form-data">
          {/* Avatar */}
          <div>
            <label className={LABEL_CLS}>{t('settings.profile.avatar')}</label>
            <div className="flex items-center gap-4">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="avatar"
                  className="w-16 h-16 rounded-full object-cover border border-zinc-700"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center text-lg font-mono font-bold text-zinc-300">
                  {initials(user.display_name || user.email)}
                </div>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="text-xs font-mono text-emerald-400 hover:text-emerald-300 border border-zinc-700 rounded px-3 py-1 transition-colors"
                >
                  {t('settings.profile.change_avatar')}
                </button>
                <p className="mt-1 text-[10px] font-mono text-zinc-500">{t('settings.profile.avatar_hint')}</p>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Display name */}
          <div>
            <label htmlFor="display_name" className={LABEL_CLS}>{t('settings.profile.display_name')}</label>
            <input
              id="display_name"
              type="text"
              value={data.display_name}
              onChange={e => setData('display_name', e.target.value)}
              className={INPUT_CLS}
              placeholder={t('settings.profile.display_name_placeholder')}
              maxLength={80}
            />
            {errors.display_name && (
              <p className="mt-1 text-xs font-mono text-red-400">{errors.display_name}</p>
            )}
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="bio" className={LABEL_CLS}>{t('settings.profile.bio')}</label>
            <textarea
              id="bio"
              value={data.bio}
              onChange={e => setData('bio', e.target.value)}
              className={`${INPUT_CLS} resize-none`}
              rows={3}
              placeholder={t('settings.profile.bio_placeholder')}
              maxLength={500}
            />
            {errors.bio && (
              <p className="mt-1 text-xs font-mono text-red-400">{errors.bio}</p>
            )}
          </div>

          {/* Timezone */}
          <div>
            <label htmlFor="timezone" className={LABEL_CLS}>{t('settings.profile.timezone')}</label>
            <select
              id="timezone"
              value={data.timezone}
              onChange={e => setData('timezone', e.target.value)}
              className={INPUT_CLS}
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            {errors.timezone && (
              <p className="mt-1 text-xs font-mono text-red-400">{errors.timezone}</p>
            )}
          </div>

          {/* Language */}
          <div>
            <label htmlFor="preferred_locale" className={LABEL_CLS}>LANGUAGE</label>
            <select
              id="preferred_locale"
              value={data.preferred_locale}
              onChange={e => setData('preferred_locale', e.target.value)}
              className={INPUT_CLS}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>

          {/* Notification preferences */}
          <div>
            <p className={LABEL_CLS}>{t('settings.profile.notifications')}</p>
            <div className="space-y-2 mt-1">
              <Toggle
                id="weekly_digest"
                label={t('settings.profile.weekly_digest')}
                checked={!!data.notification_preferences?.weekly_digest}
                onChange={() => handleNotifToggle('weekly_digest')}
              />
              <Toggle
                id="session_summary_email"
                label={t('settings.profile.session_summary_email')}
                checked={!!data.notification_preferences?.session_summary_email}
                onChange={() => handleNotifToggle('session_summary_email')}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={processing}
            className="bg-emerald-800/60 hover:bg-emerald-700/80 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-200 border border-emerald-700/40 font-mono text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {processing ? t('settings.profile.saving') : t('settings.profile.save')}
          </button>
        </form>
      </div>
    </Layout>
  )
}

function Toggle({ id, label, checked, onChange }) {
  return (
    <label htmlFor={id} className="flex items-center gap-3 cursor-pointer group">
      <div className="relative">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={[
            'w-9 h-5 rounded-full border transition-colors',
            checked
              ? 'bg-emerald-700/70 border-emerald-600'
              : 'bg-zinc-700 border-zinc-600',
          ].join(' ')}
        />
        <div
          className={[
            'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </div>
      <span className="text-xs font-mono text-zinc-300 group-hover:text-zinc-100 transition-colors">
        {label}
      </span>
    </label>
  )
}
